import { logError } from "@rentiohq/shared-frontend/dist/logger/logger";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { ITemplate } from "@rentiohq/shared-frontend/dist/redux/template/template.types";
import { IBankAccount } from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount";
import { IIndexInfoSimulation } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.api";
import { checkIfEpcExpired } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.utils";
import { PROPERTY_TYPES } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import {
  ITemplateDocument,
  ITemplateDocumentDetail,
} from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.types";
import { IBroker } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import {
  EContractCostsPriceType,
  EContractIbanisationStatus,
  EContractMemberTypes,
  EEpcBasedIndexationType,
  IContract,
  IIndexInfoAvailable,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPropertyEPCLabel,
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  addDays,
  differenceInDays,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatIban } from "@rentiohq/shared-frontend/dist/utils/iban.utils";
import { getAmountWithVat } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  getMembersWithOneOfRoles,
  getMembersWithRole,
} from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import { IPreferences } from "@rentiohq/web-shared/dist/redux/system/system.types";
import { EDefaultVariableId } from "@rentiohq/web-shared/dist/scenes/TemplateEditor/components/Editor/Editor.config";
import {
  IDocumentConfig,
  IFields,
  IVariablesData,
} from "@rentiohq/web-shared/dist/scenes/TemplateEditor/components/Editor/Editor.types";
import {
  getTenantEditableVariableIds,
  parseFields,
} from "@rentiohq/web-shared/dist/scenes/TemplateEditor/components/Editor/Editor.utils";
import { getOptionTranslationKey } from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.options";
import { floor, round } from "lodash";

export const getDefaultFilename = (params: {
  type: "contract" | string;
  template?: ITemplate;
  templateDocument?: ITemplateDocument;
  property: IProperty;
}) => {
  const { type, template, templateDocument, property } = params;

  if (type === "contract") {
    return getLocalizedText(
      "create_template_document.modal.name_template.default_filename_contract",
      {
        address: formatAddress(property),
      },
    );
  }

  return template?.name || templateDocument?.name || "File";
};

export const getDefaultDocumentConfig = (params: { brandColor?: string }) => {
  const { brandColor } = params;

  const result: IDocumentConfig = {
    brandColor,
  };

  return JSON.stringify(result);
};

// Every `EDefaultVariableId` value, as the plain strings both callers below
// compare a stored `variablesData` key against.
const DEFAULT_VARIABLE_IDS = Object.values(EDefaultVariableId) as string[];

export const getDefaultVariablesData = (params: {
  template?: ITemplate;
  legalContact?: IContact;
  meMaster?: IContact;
  broker?: IBroker;
  brokerLogoDocumentId?: string;
  property: IProperty;
  propertyAmenities?: IPropertyAmenity[];
  contract?: IContract;
  indexInfoAvailable?: IIndexInfoAvailable;
  indexInfoSimulation?: IIndexInfoSimulation;
  indexInfoRequest?: {
    newPrice: number;
    isRetroactive: boolean;
    forceFullIndexation: boolean;
  };
  contacts: IContact[];
  fields: IFields;
  preferences: IPreferences;
  rentPaymentOrder?: IPaymentOrder;

  ibanisationBankAccount?: IBankAccount;
  ownerBankAccount?: IBankAccount;
  variablesDataToOverride?: IVariablesData;
}) => {
  const {
    legalContact,
    meMaster,
    broker,
    brokerLogoDocumentId,
    property,
    propertyAmenities,
    contract,
    indexInfoAvailable,
    indexInfoSimulation,
    indexInfoRequest,
    contacts,
    fields,
    preferences,
    rentPaymentOrder,
    ibanisationBankAccount,
    variablesDataToOverride,
    ownerBankAccount,
    template,
  } = params;

  const translateByTemplateLanguage = (key: string) =>
    getLocalizedText(key, undefined, undefined, template?.locale);

  const result: Record<
    EDefaultVariableId,
    (string | number | boolean | Date | undefined | null)[]
  > = {
    [EDefaultVariableId.Now]: [new Date()],
    [EDefaultVariableId.CreatedAt]: [new Date()],
    [EDefaultVariableId.RentDepositPrice]: [],
    [EDefaultVariableId.RentDepositMonths]: [],
    [EDefaultVariableId.RentDepositWithRentio]: [],
    [EDefaultVariableId.RentDepositBankAccountNumber]: [],
    [EDefaultVariableId.LocationDescriptionPerson]: [],
    [EDefaultVariableId.BrokerEnabled]: [],
    [EDefaultVariableId.BrokerBiv]: [],
    [EDefaultVariableId.BrokerOfficeName]: [],
    [EDefaultVariableId.BrokerFullName]: [],
    [EDefaultVariableId.BrokerFirstName]: [],
    [EDefaultVariableId.BrokerLastName]: [],
    [EDefaultVariableId.BrokerBirthPlace]: [],
    [EDefaultVariableId.BrokerBirthDate]: [],
    [EDefaultVariableId.BrokerAddressFull]: [],
    [EDefaultVariableId.BrokerAddressStreet]: [],
    [EDefaultVariableId.BrokerAddressNumber]: [],
    [EDefaultVariableId.BrokerAddressBox]: [],
    [EDefaultVariableId.BrokerAddressZip]: [],
    [EDefaultVariableId.BrokerAddressCity]: [],
    [EDefaultVariableId.BrokerAddressCountry]: [],
    [EDefaultVariableId.BrokerLogo]: [],
    [EDefaultVariableId.BrokerIsSignerOnBehalfOfOwners]: [],
    [EDefaultVariableId.BrokerSignature]: [],
    [EDefaultVariableId.BrokerCivilLiabilityAtCompany]: [],
    [EDefaultVariableId.BrokerCivilLiabilityPolicyNumber]: [],
    [EDefaultVariableId.OwnerIsCompany]: [],
    [EDefaultVariableId.OwnerFullName]: [],
    [EDefaultVariableId.OwnerFirstName]: [],
    [EDefaultVariableId.OwnerLastName]: [],
    [EDefaultVariableId.OwnerBirthPlace]: [],
    [EDefaultVariableId.OwnerBirthDate]: [],
    [EDefaultVariableId.OwnerCompanyName]: [],
    [EDefaultVariableId.OwnerCompanyVat]: [],
    [EDefaultVariableId.OwnerCompanyRole]: [],
    [EDefaultVariableId.OwnerAddressFull]: [],
    [EDefaultVariableId.OwnerAddressStreet]: [],
    [EDefaultVariableId.OwnerAddressNumber]: [],
    [EDefaultVariableId.OwnerAddressBox]: [],
    [EDefaultVariableId.OwnerAddressZip]: [],
    [EDefaultVariableId.OwnerAddressCity]: [],
    [EDefaultVariableId.OwnerAddressCountry]: [],
    [EDefaultVariableId.OwnerCompanyAddressFull]: [],
    [EDefaultVariableId.OwnerSignature]: [],
    [EDefaultVariableId.OwnerEmail]: [],
    [EDefaultVariableId.OwnerPhone]: [],
    [EDefaultVariableId.OwnerNationality]: [],
    [EDefaultVariableId.OwnerNationalRegisterNumber]: [],
    [EDefaultVariableId.OwnerPassportNumber]: [],
    [EDefaultVariableId.OwnerMaritalStatus]: [],
    [EDefaultVariableId.OwnerBankAccountNumber]: [],
    [EDefaultVariableId.TenantIsCompany]: [],
    [EDefaultVariableId.TenantFullName]: [],
    [EDefaultVariableId.TenantFirstName]: [],
    [EDefaultVariableId.TenantLastName]: [],
    [EDefaultVariableId.TenantBirthPlace]: [],
    [EDefaultVariableId.TenantBirthDate]: [],
    [EDefaultVariableId.TenantCompanyName]: [],
    [EDefaultVariableId.TenantCompanyVat]: [],
    [EDefaultVariableId.TenantCompanyRole]: [],
    [EDefaultVariableId.TenantAddressFull]: [],
    [EDefaultVariableId.TenantAddressStreet]: [],
    [EDefaultVariableId.TenantAddressNumber]: [],
    [EDefaultVariableId.TenantAddressBox]: [],
    [EDefaultVariableId.TenantAddressZip]: [],
    [EDefaultVariableId.TenantAddressCity]: [],
    [EDefaultVariableId.TenantAddressCountry]: [],
    [EDefaultVariableId.TenantCompanyAddressFull]: [],
    [EDefaultVariableId.TenantSignature]: [],
    [EDefaultVariableId.TenantEmail]: [],
    [EDefaultVariableId.TenantPhone]: [],
    [EDefaultVariableId.TenantNationality]: [],
    [EDefaultVariableId.TenantNationalRegisterNumber]: [],
    [EDefaultVariableId.TenantPassportNumber]: [],
    [EDefaultVariableId.TenantMaritalStatus]: [],
    [EDefaultVariableId.ParentIsCompany]: [],
    [EDefaultVariableId.ParentFullName]: [],
    [EDefaultVariableId.ParentFirstName]: [],
    [EDefaultVariableId.ParentLastName]: [],
    [EDefaultVariableId.ParentBirthPlace]: [],
    [EDefaultVariableId.ParentBirthDate]: [],
    [EDefaultVariableId.ParentCompanyName]: [],
    [EDefaultVariableId.ParentCompanyVat]: [],
    [EDefaultVariableId.ParentCompanyRole]: [],
    [EDefaultVariableId.ParentAddressFull]: [],
    [EDefaultVariableId.ParentAddressStreet]: [],
    [EDefaultVariableId.ParentAddressNumber]: [],
    [EDefaultVariableId.ParentAddressBox]: [],
    [EDefaultVariableId.ParentAddressZip]: [],
    [EDefaultVariableId.ParentAddressCity]: [],
    [EDefaultVariableId.ParentAddressCountry]: [],
    [EDefaultVariableId.ParentCompanyAddressFull]: [],
    [EDefaultVariableId.ParentSignature]: [],
    [EDefaultVariableId.ParentEmail]: [],
    [EDefaultVariableId.ParentPhone]: [],
    [EDefaultVariableId.ParentNationality]: [],
    [EDefaultVariableId.ParentNationalRegisterNumber]: [],
    [EDefaultVariableId.ParentPassportNumber]: [],
    [EDefaultVariableId.ParentMaritalStatus]: [],
    [EDefaultVariableId.PropertyType]: [],
    [EDefaultVariableId.PropertyAddressFull]: [],
    [EDefaultVariableId.PropertyAddressStreet]: [],
    [EDefaultVariableId.PropertyAddressNumber]: [],
    [EDefaultVariableId.PropertyAddressBox]: [],
    [EDefaultVariableId.PropertyAddressZip]: [],
    [EDefaultVariableId.PropertyAddressCity]: [],
    [EDefaultVariableId.PropertyAddressCountry]: [],
    [EDefaultVariableId.PropertyDescription]: [],
    [EDefaultVariableId.PropertyName]: [],
    [EDefaultVariableId.PropertyReference]: [],
    [EDefaultVariableId.PropertySyndic]: [],
    [EDefaultVariableId.PropertyHasOilTank]: [],
    [EDefaultVariableId.PropertyOilTankIsBuried]: [],
    [EDefaultVariableId.PropertyOilTankVolume]: [],
    [EDefaultVariableId.PropertySmokeDetectorCount]: [],
    [EDefaultVariableId.PropertyEpcLabel]: [],
    [EDefaultVariableId.PropertyEpcValue]: [],
    [EDefaultVariableId.PropertyEpcExpirationDate]: [],
    [EDefaultVariableId.PropertyEpcIsExpired]: [],
    [EDefaultVariableId.PropertyEpcIsUnknown]: [],
    [EDefaultVariableId.PropertyEpcIsValid]: [],
    [EDefaultVariableId.PropertyUtilityCounterWaterAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterWaterIsCollective]: [],
    [EDefaultVariableId.PropertyUtilityCounterNaturalGasAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterNaturalGasIsCollective]: [],
    [EDefaultVariableId.PropertyUtilityCounterElectricityAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterElectricityIsCollective]: [],
    [EDefaultVariableId.PropertyUsagePrivatePercentage]: [],
    [EDefaultVariableId.PropertyUsageProfessionalPercentage]: [],
    [EDefaultVariableId.PropertyTechnicalManagers]: [],
    [EDefaultVariableId.PropertyFinancialManagers]: [],
    [EDefaultVariableId.PropertyConstructionYear]: [],
    [EDefaultVariableId.PropertyElectricityType]: [],
    [EDefaultVariableId.PropertyElectricityMeterType]: [],
    [EDefaultVariableId.PropertyNaturalGasAvailable]: [],
    [EDefaultVariableId.PropertyNaturalGasMeterType]: [],
    [EDefaultVariableId.PropertyEpcCertificateId]: [],
    [EDefaultVariableId.PropertyBuildingAdjoined]: [],
    [EDefaultVariableId.PropertyBuildingSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingGardenSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingTerraceSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingFloor]: [],
    [EDefaultVariableId.PropertyBuildingFloorCount]: [],
    [EDefaultVariableId.PropertyRoomCountBedroom]: [],
    [EDefaultVariableId.PropertyRoomCountBathroom]: [],
    [EDefaultVariableId.PropertyRoomCountGarage]: [],
    [EDefaultVariableId.PropertyBuildingHasStorage]: [],
    [EDefaultVariableId.PropertyIsFurnished]: [],
    [EDefaultVariableId.PropertyHasOven]: [],
    [EDefaultVariableId.PropertyHasFridge]: [],
    [EDefaultVariableId.PropertyHasDishwasher]: [],
    [EDefaultVariableId.PropertyHasRangeHood]: [],
    [EDefaultVariableId.PropertyHasLift]: [],
    [EDefaultVariableId.PropertyHasDoubleGlazing]: [],
    [EDefaultVariableId.PropertyHasCentralHeating]: [],
    [EDefaultVariableId.PropertyHeatingType]: [],
    [EDefaultVariableId.PropertyCostsComment]: [],
    [EDefaultVariableId.PropertyLivingSurfaceArea]: [],
    [EDefaultVariableId.PropertyGroundSurfaceArea]: [],
    [EDefaultVariableId.PropertyHasGarden]: [],
    [EDefaultVariableId.PropertyGardenOrientation]: [],
    [EDefaultVariableId.PropertyHasTerrace]: [],
    [EDefaultVariableId.PropertyTerraceOrientation]: [],
    [EDefaultVariableId.PropertyHasGardenShed]: [],
    [EDefaultVariableId.PropertyHasPool]: [],
    [EDefaultVariableId.PropertyNumberOfParkingsOutside]: [],
    [EDefaultVariableId.PropertyNumberOfToilets]: [],
    [EDefaultVariableId.PropertyNumberOfShowerRooms]: [],
    [EDefaultVariableId.PropertyHasAttic]: [],
    [EDefaultVariableId.PropertyKitchenType]: [],
    [EDefaultVariableId.PropertyFloorType]: [],
    [EDefaultVariableId.PropertyBuildingHasHardMaterials]: [],
    [EDefaultVariableId.PropertyNumberOfParkingsInside]: [],
    [EDefaultVariableId.PropertyHasAirConditioning]: [],
    [EDefaultVariableId.PropertyHasAlarm]: [],
    [EDefaultVariableId.PropertyHasVideophone]: [],
    [EDefaultVariableId.PropertyHasSmokeDetectors]: [],
    [EDefaultVariableId.PropertyAccessibleForDisabled]: [],
    [EDefaultVariableId.PropertyHeatingDistribution]: [],
    [EDefaultVariableId.PropertyGlazingType]: [],
    [EDefaultVariableId.PropertyWindowFrameMaterial]: [],
    [EDefaultVariableId.PropertyHasInsulation]: [],
    [EDefaultVariableId.PropertyHasSolarPanels]: [],
    [EDefaultVariableId.PropertyIsLowEnergy]: [],
    [EDefaultVariableId.PropertyIsPassiveHouse]: [],
    [EDefaultVariableId.PropertyCo2Emission]: [],
    [EDefaultVariableId.PropertyELevel]: [],
    [EDefaultVariableId.PropertyKLevel]: [],
    [EDefaultVariableId.PropertyPetsAllowed]: [],
    [EDefaultVariableId.PropertyPetsAllowedDetail]: [],
    [EDefaultVariableId.PropertySmokingAllowed]: [],
    [EDefaultVariableId.PropertyCondition]: [],
    [EDefaultVariableId.PropertyRenovationYear]: [],
    [EDefaultVariableId.PropertyEnvironmentType]: [],
    [EDefaultVariableId.PropertyFrontWidth]: [],
    [EDefaultVariableId.PropertyVolume]: [],
    [EDefaultVariableId.PropertyRoofType]: [],
    [EDefaultVariableId.PropertyRoofMaterial]: [],
    [EDefaultVariableId.PropertyFloodZoneGScore]: [],
    [EDefaultVariableId.PropertyFloodZonePScore]: [],
    [EDefaultVariableId.PropertyConformityCertificate]: [],
    [EDefaultVariableId.PropertyElectricityCertificate]: [],
    [EDefaultVariableId.PropertyAsbestosCertificate]: [],
    [EDefaultVariableId.PropertyAsbestosCertificateDate]: [],
    [EDefaultVariableId.PropertyVirtualTourUrl]: [],
    [EDefaultVariableId.PropertyVideoUrl]: [],
    [EDefaultVariableId.PropertyModel3dUrl]: [],
    [EDefaultVariableId.PropertyFloorPlanUrl]: [],
    [EDefaultVariableId.ContractStartDate]: [],
    [EDefaultVariableId.ContractStopDate]: [],
    [EDefaultVariableId.ContractPaymentRepetition]: [],
    [EDefaultVariableId.ContractPriceVat]: [],
    [EDefaultVariableId.ContractStartPrice]: [],
    [EDefaultVariableId.ContractStartPriceIncludingVat]: [],
    [EDefaultVariableId.ContractCostsCommonVat]: [],
    [EDefaultVariableId.ContractStartCostsCommonFixed]: [],
    [EDefaultVariableId.ContractStartCostsCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractStartCostsCommonProvision]: [],
    [EDefaultVariableId.ContractStartCostsCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCostsNonCommonVat]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonFixed]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonProvision]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentPrice]: [],
    [EDefaultVariableId.ContractCurrentPriceIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonFixed]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonProvision]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonFixed]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonProvision]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractPriceTypeCostsCommon]: [],
    [EDefaultVariableId.ContractPriceTypeCostsNonCommon]: [],
    [EDefaultVariableId.ContractStartIndexDate]: [],
    [EDefaultVariableId.ContractStartIndexNumber]: [],
    [EDefaultVariableId.ContractDurationYears]: [],
    [EDefaultVariableId.ContractIndexStartIndex]: [],
    [EDefaultVariableId.ContractIndexNewIndex]: [],
    [EDefaultVariableId.ContractIndexCorrectionFactor]: [],
    [EDefaultVariableId.ContractIndexEpcBasedIndexType]: [],
    [EDefaultVariableId.ContractIndexStartNextPaymentPeriod]: [],
    [EDefaultVariableId.ContractIndexNewPriceWasAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewPriceOriginal]: [],
    [EDefaultVariableId.ContractIndexNewCommonCostsOriginal]: [],
    [EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal]: [],
    [EDefaultVariableId.ContractIndexNewPriceAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewCommonCostsAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted]: [],
    [EDefaultVariableId.ContractIndexTotalPriceOriginal]: [],
    [EDefaultVariableId.ContractIndexTotalPriceAdjusted]: [],
    [EDefaultVariableId.ContractIndexForceFullIndexation]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodStart]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodEnd]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodPrice]: [],
    [EDefaultVariableId.ContractIndexPaymentsWithRentio]: [],
    [EDefaultVariableId.ContractIndexPaymentOrderNewDueDate]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountIban]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountBic]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountReference]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodStart]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodEnd]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodPrice]: [],
    [EDefaultVariableId.ContractIndexCorrectionAndRetroSum]: [],
    [EDefaultVariableId.ContractIndexRetroactivePayments]: [],
    [EDefaultVariableId.EanCodeElectricity]: [],
    [EDefaultVariableId.EanCodeGas]: [],
    [EDefaultVariableId.MeterNumberGas]: [],
    [EDefaultVariableId.MeterNumberElectricity]: [],
    [EDefaultVariableId.MeterNumberWater]: [],
    [EDefaultVariableId.AdditionalCostDescription]: [],
    [EDefaultVariableId.AdditionalCostStartPrice]: [],
    [EDefaultVariableId.AdditionalCostCorrectionFactor]: [],
    [EDefaultVariableId.AdditionalCostProposedIncrease]: [],
    [EDefaultVariableId.AdditionalCostNewPrice]: [],
  };

  fields.variables.forEach(variable => {
    if (DEFAULT_VARIABLE_IDS.includes(variable.id)) {
      return;
    }

    result[variable.id as EDefaultVariableId] = [
      variable.defaultDate ||
        variable.defaultNumber ||
        variable.defaultText ||
        variable.defaultToggle,
    ];
  });

  // TODO: Legal or financial contact?
  // Broker (legal contact & broker objects)
  if (legalContact) {
    result[EDefaultVariableId.BrokerEnabled] = [true];
    result[EDefaultVariableId.BrokerBiv] = [legalContact.bivNumber];
    result[EDefaultVariableId.BrokerFullName] = [
      [legalContact.firstname, legalContact.lastname].filter(Boolean).join(" "),
    ];
    result[EDefaultVariableId.BrokerFirstName] = [legalContact.firstname];
    result[EDefaultVariableId.BrokerLastName] = [legalContact.lastname];
    result[EDefaultVariableId.BrokerBirthPlace] = [legalContact.placeOfBirth];
    result[EDefaultVariableId.BrokerBirthDate] = [legalContact.dateOfBirth];
    result[EDefaultVariableId.BrokerAddressFull] = [
      formatAddress(legalContact, true, true, true, true),
    ];
    result[EDefaultVariableId.BrokerAddressStreet] = [legalContact.street];
    result[EDefaultVariableId.BrokerAddressNumber] = [legalContact.number];
    result[EDefaultVariableId.BrokerAddressBox] = [legalContact.box];
    result[EDefaultVariableId.BrokerAddressZip] = [legalContact.zip];
    result[EDefaultVariableId.BrokerAddressCity] = [legalContact.city];

    const country = translateByTemplateLanguage(
      `system.country.${legalContact.countryId}`.toLowerCase(),
    );
    result[EDefaultVariableId.BrokerAddressCountry] = [country];
  }

  if (broker) {
    result[EDefaultVariableId.BrokerOfficeName] = [broker.name];
    result[EDefaultVariableId.BrokerLogo] = [brokerLogoDocumentId];
  }

  if (meMaster) {
    result[EDefaultVariableId.BrokerSignature] = [meMaster.accountId];
  }

  // Owners
  const ownerAccounts = contract
    ? getMembersWithRole(
        contract?.members || [],
        EContractMemberTypes.Owner,
      ).map(owner => owner.account)
    : getMembersWithRole(
        property?.members || [],
        EPropertyMemberTypes.Owner,
      ).map(owner => owner.account);

  ownerAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.OwnerIsCompany].push(!!x.company);
    result[EDefaultVariableId.OwnerFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.OwnerFirstName].push(x.firstname);
    result[EDefaultVariableId.OwnerLastName].push(x.lastname);
    result[EDefaultVariableId.OwnerBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.OwnerBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.OwnerCompanyName].push(x.company);
    result[EDefaultVariableId.OwnerCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.OwnerCompanyRole].push(undefined);
    result[EDefaultVariableId.OwnerAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.OwnerAddressStreet].push(contact?.street);
    result[EDefaultVariableId.OwnerAddressNumber].push(contact?.number);
    result[EDefaultVariableId.OwnerAddressBox].push(contact?.box);
    result[EDefaultVariableId.OwnerAddressZip].push(contact?.zip);
    result[EDefaultVariableId.OwnerAddressCity].push(contact?.city);

    const country = contact
      ? translateByTemplateLanguage(
          `system.country.${contact.countryId}`.toLowerCase(),
        )
      : undefined;
    result[EDefaultVariableId.OwnerAddressCountry].push(country);
    result[EDefaultVariableId.OwnerCompanyAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );

    result[EDefaultVariableId.OwnerSignature].push(account.id);
    result[EDefaultVariableId.OwnerEmail].push(contact?.email);
    result[EDefaultVariableId.OwnerPhone].push(contact?.phone);
    result[EDefaultVariableId.OwnerNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.OwnerNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.OwnerPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  result[EDefaultVariableId.OwnerBankAccountNumber].push(
    formatIban(ownerBankAccount?.iban),
  );

  // Tenants
  const tenantAccounts = getMembersWithOneOfRoles(contract?.members || [], [
    EContractMemberTypes.Tenant,
  ]).map(owner => owner.account);

  tenantAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.TenantIsCompany].push(!!x.company);
    result[EDefaultVariableId.TenantFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.TenantFirstName].push(x.firstname);
    result[EDefaultVariableId.TenantLastName].push(x.lastname);
    result[EDefaultVariableId.TenantBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.TenantBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.TenantCompanyName].push(x.company);
    result[EDefaultVariableId.TenantCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.TenantCompanyRole].push(undefined);
    result[EDefaultVariableId.TenantAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.TenantAddressStreet].push(contact?.street);
    result[EDefaultVariableId.TenantAddressNumber].push(contact?.number);
    result[EDefaultVariableId.TenantAddressBox].push(contact?.box);
    result[EDefaultVariableId.TenantAddressZip].push(contact?.zip);
    result[EDefaultVariableId.TenantAddressCity].push(contact?.city);
    result[EDefaultVariableId.TenantAddressCountry].push(contact?.countryId);
    result[EDefaultVariableId.TenantCompanyAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.TenantSignature].push(account.id);
    result[EDefaultVariableId.TenantEmail].push(contact?.email);
    result[EDefaultVariableId.TenantPhone].push(contact?.phone);
    result[EDefaultVariableId.TenantNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.TenantNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.TenantPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  // Parents
  const parentAccounts = getMembersWithOneOfRoles(contract?.members || [], [
    EContractMemberTypes.Parent,
  ]).map(owner => owner.account);

  parentAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.ParentIsCompany].push(!!x.company);
    result[EDefaultVariableId.ParentFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.ParentFirstName].push(x.firstname);
    result[EDefaultVariableId.ParentLastName].push(x.lastname);
    result[EDefaultVariableId.ParentBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.ParentBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.ParentCompanyName].push(x.company);
    result[EDefaultVariableId.ParentCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.ParentCompanyRole].push(undefined);
    result[EDefaultVariableId.ParentAddressFull].push(
      contact ? formatAddress(contact) : undefined,
    );
    result[EDefaultVariableId.ParentAddressStreet].push(contact?.street);
    result[EDefaultVariableId.ParentAddressNumber].push(contact?.number);
    result[EDefaultVariableId.ParentAddressBox].push(contact?.box);
    result[EDefaultVariableId.ParentAddressZip].push(contact?.zip);
    result[EDefaultVariableId.ParentAddressCity].push(contact?.city);
    result[EDefaultVariableId.ParentAddressCountry].push(contact?.countryId);
    result[EDefaultVariableId.ParentCompanyAddressFull].push(
      contact ? formatAddress(contact) : undefined,
    );
    result[EDefaultVariableId.ParentSignature].push(account.id);
    result[EDefaultVariableId.ParentEmail].push(contact?.email);
    result[EDefaultVariableId.ParentPhone].push(contact?.phone);
    result[EDefaultVariableId.ParentNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.ParentNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.ParentPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  // Property
  const propertyType = PROPERTY_TYPES.find(type => type.id === property.typeId);
  result[EDefaultVariableId.PropertyType].push(
    propertyType
      ? translateByTemplateLanguage(`property.type.${propertyType.label}`)
      : undefined,
  );

  result[EDefaultVariableId.PropertyAddressFull].push(
    formatAddress(property, true, true, true, true),
  );
  result[EDefaultVariableId.PropertyAddressStreet].push(property.street);
  result[EDefaultVariableId.PropertyAddressNumber].push(property.number);
  result[EDefaultVariableId.PropertyAddressBox].push(property.box);
  result[EDefaultVariableId.PropertyAddressZip].push(property.zip);
  result[EDefaultVariableId.PropertyAddressCity].push(property.city);
  result[EDefaultVariableId.PropertyName].push(property.name);
  result[EDefaultVariableId.PropertyReference].push(property.reference);
  result[EDefaultVariableId.EanCodeElectricity].push(property.electricityEan);
  result[EDefaultVariableId.EanCodeGas].push(property.naturalGasEan);
  result[EDefaultVariableId.MeterNumberGas].push(
    property.naturalGasMeterNumber,
  );
  result[EDefaultVariableId.MeterNumberElectricity].push(
    property.electricityMeterNumberBoth,
  );
  result[EDefaultVariableId.MeterNumberWater].push(property.waterMeterNumber);

  const { epcLabel } = property;

  result[EDefaultVariableId.PropertyEpcLabel].push(
    translateByTemplateLanguage(
      `epc.label.${(epcLabel || EPropertyEPCLabel.Unknown).toLowerCase()}`,
    ),
  );

  const hasEpc =
    epcLabel &&
    ![EPropertyEPCLabel.Unknown, EPropertyEPCLabel.NoEPCAvailable].includes(
      epcLabel,
    );

  if (hasEpc) {
    result[EDefaultVariableId.PropertyEpcValue].push(property.epcValue);
    result[EDefaultVariableId.PropertyEpcExpirationDate].push(
      property.epcExpirationDate,
    );
    result[EDefaultVariableId.PropertyEpcIsExpired].push(
      !!checkIfEpcExpired(property.epcExpirationDate),
    );
    result[EDefaultVariableId.PropertyEpcIsValid].push(
      !checkIfEpcExpired(property.epcExpirationDate),
    );
  } else {
    result[EDefaultVariableId.PropertyEpcValue].push(undefined);
    result[EDefaultVariableId.PropertyEpcExpirationDate].push(undefined);
    result[EDefaultVariableId.PropertyEpcIsUnknown].push(true);
  }

  const country = translateByTemplateLanguage(
    `system.country.${property.countryId || "BE"}`.toLowerCase(),
  );
  result[EDefaultVariableId.PropertyAddressCountry].push(country);

  const technicalManagers = getMembersWithRole(
    property?.members || [],
    EPropertyMemberTypes.TechnicalManager,
  )
    .map(member => member.account)
    .map(account =>
      contacts.find(contact => contact.accountIds.includes(account.id)),
    )
    .filter(Boolean)
    .map(x => (x ? getName(x) : ""))
    .join(", ");

  const financialManagers = getMembersWithRole(
    property?.members || [],
    EPropertyMemberTypes.FinancialManager,
  )
    .map(member => member.account)
    .map(account =>
      contacts.find(contact => contact.accountIds.includes(account.id)),
    )
    .filter(Boolean)
    .map(x => (x ? getName(x) : ""))
    .join(", ");

  result[EDefaultVariableId.PropertyTechnicalManagers].push(technicalManagers);
  result[EDefaultVariableId.PropertyFinancialManagers].push(financialManagers);

  result[EDefaultVariableId.PropertyConstructionYear].push(
    property.constructionYear,
  );

  result[EDefaultVariableId.PropertyElectricityType].push(
    property.electricityType
      ? translateByTemplateLanguage(
          `move.option.electricity_type.${property.electricityType}`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyElectricityMeterType].push(
    property.electricityMeterType
      ? translateByTemplateLanguage(
          `move.option.electricity_meter_type.${property.electricityMeterType}`.toLowerCase(),
        )
      : undefined,
  );

  result[EDefaultVariableId.PropertyNaturalGasAvailable].push(
    property.naturalGasAvailable
      ? translateByTemplateLanguage(
          `move.option.natural_gas_type.${property.naturalGasAvailable}`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyNaturalGasMeterType].push(
    property.naturalGasMeterType
      ? translateByTemplateLanguage(
          `move.option.natural_gas_meter_type.${property.naturalGasMeterType}`.toLowerCase(),
        )
      : undefined,
  );

  result[EDefaultVariableId.PropertyEpcCertificateId].push(
    property.epcCertificateId,
  );

  // Exterior - from propertyAmenities
  const buildingAdjoined = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.buildingAdjoined,
  );
  result[EDefaultVariableId.PropertyBuildingAdjoined].push(
    buildingAdjoined
      ? translateByTemplateLanguage(
          `property.adjoined.option.${buildingAdjoined}.label`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyBuildingSurfaceArea].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingSurfaceArea),
  );
  result[EDefaultVariableId.PropertyBuildingGardenSurfaceArea].push(
    property.buildingGardenSurfaceArea,
  );
  result[EDefaultVariableId.PropertyBuildingTerraceSurfaceArea].push(
    property.buildingTerraceSurfaceArea,
  );

  // Interior - from propertyAmenities
  result[EDefaultVariableId.PropertyBuildingFloor].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingFloor),
  );
  result[EDefaultVariableId.PropertyBuildingFloorCount].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingFloorCount),
  );
  result[EDefaultVariableId.PropertyRoomCountBedroom].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountBedroom),
  );
  result[EDefaultVariableId.PropertyRoomCountBathroom].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountBathroom),
  );
  result[EDefaultVariableId.PropertyRoomCountGarage].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountGarage),
  );
  result[EDefaultVariableId.PropertyBuildingHasStorage].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.buildingHasStorage),
  );
  result[EDefaultVariableId.PropertyIsFurnished].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isFurnished),
  );

  // Amenities - from propertyAmenities
  result[EDefaultVariableId.PropertyHasOven].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasOven),
  );
  result[EDefaultVariableId.PropertyHasFridge].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasFridge),
  );
  result[EDefaultVariableId.PropertyHasDishwasher].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDishwasher),
  );
  result[EDefaultVariableId.PropertyHasRangeHood].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasRangeHood),
  );
  result[EDefaultVariableId.PropertyHasLift].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasLift),
  );

  // Energy - from propertyAmenities
  result[EDefaultVariableId.PropertyHasDoubleGlazing].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDoubleGlazing),
  );
  result[EDefaultVariableId.PropertyHasCentralHeating].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasCentralHeating),
  );
  const heatingType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.heatingType,
  );
  result[EDefaultVariableId.PropertyHeatingType].push(
    heatingType
      ? translateByTemplateLanguage(
          `property.extra.option.energy.${heatingType}`.toLowerCase(),
        )
      : undefined,
  );

  // The rest of the CRM field list. A value stored as an enum is translated
  // into the document's own language, not the broker's, which is why the
  // option key is looked up rather than the label.
  result[EDefaultVariableId.PropertyDescription].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.descriptionLong),
  );
  result[EDefaultVariableId.PropertyCostsComment].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.costsComment),
  );
  result[EDefaultVariableId.PropertyLivingSurfaceArea].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.livingSurfaceArea),
  );
  result[EDefaultVariableId.PropertyGroundSurfaceArea].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.groundSurfaceArea),
  );
  result[EDefaultVariableId.PropertyHasGarden].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasGarden),
  );
  const propertyGardenOrientation = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.gardenOrientation,
  );
  result[EDefaultVariableId.PropertyGardenOrientation].push(
    propertyGardenOrientation
      ? translateByTemplateLanguage(
          getOptionTranslationKey("orientation", propertyGardenOrientation),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyHasTerrace].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasTerrace),
  );
  const propertyTerraceOrientation = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.terraceOrientation,
  );
  result[EDefaultVariableId.PropertyTerraceOrientation].push(
    propertyTerraceOrientation
      ? translateByTemplateLanguage(
          getOptionTranslationKey("orientation", propertyTerraceOrientation),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyHasGardenShed].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasGardenShed),
  );
  result[EDefaultVariableId.PropertyHasPool].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasPool),
  );
  result[EDefaultVariableId.PropertyNumberOfParkingsOutside].push(
    getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.numberOfParkingsOutside,
    ),
  );
  result[EDefaultVariableId.PropertyNumberOfToilets].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.numberOfToilets),
  );
  result[EDefaultVariableId.PropertyNumberOfShowerRooms].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.numberOfShowerRooms),
  );
  result[EDefaultVariableId.PropertyHasAttic].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasAttic),
  );
  const propertyKitchenType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.kitchenType,
  );
  result[EDefaultVariableId.PropertyKitchenType].push(
    propertyKitchenType
      ? translateByTemplateLanguage(
          getOptionTranslationKey("kitchenType", propertyKitchenType),
        )
      : undefined,
  );
  const propertyFloorType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.floorType,
  );
  result[EDefaultVariableId.PropertyFloorType].push(
    propertyFloorType
      ? translateByTemplateLanguage(
          getOptionTranslationKey("floorType", propertyFloorType),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyBuildingHasHardMaterials].push(
    getAmenityBooleanValue(
      propertyAmenities,
      AMENITY_NAMES.buildingHasHardMaterials,
    ),
  );
  result[EDefaultVariableId.PropertyNumberOfParkingsInside].push(
    getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.numberOfParkingsInside,
    ),
  );
  result[EDefaultVariableId.PropertyHasAirConditioning].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasAirConditioning),
  );
  result[EDefaultVariableId.PropertyHasAlarm].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasAlarm),
  );
  result[EDefaultVariableId.PropertyHasVideophone].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasVideophone),
  );
  result[EDefaultVariableId.PropertyHasSmokeDetectors].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasSmokeDetectors),
  );
  const propertyAccessibleForDisabled = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.accessibleForDisabled,
  );
  result[EDefaultVariableId.PropertyAccessibleForDisabled].push(
    propertyAccessibleForDisabled
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "yesNoUnknown",
            propertyAccessibleForDisabled,
          ),
        )
      : undefined,
  );
  const propertyHeatingDistribution = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.heatingDistribution,
  );
  result[EDefaultVariableId.PropertyHeatingDistribution].push(
    propertyHeatingDistribution
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "heatingDistribution",
            propertyHeatingDistribution,
          ),
        )
      : undefined,
  );
  const propertyGlazingType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.glazingType,
  );
  result[EDefaultVariableId.PropertyGlazingType].push(
    propertyGlazingType
      ? translateByTemplateLanguage(
          getOptionTranslationKey("glazingType", propertyGlazingType),
        )
      : undefined,
  );
  const propertyWindowFrameMaterial = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.windowFrameMaterial,
  );
  result[EDefaultVariableId.PropertyWindowFrameMaterial].push(
    propertyWindowFrameMaterial
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "windowFrameMaterial",
            propertyWindowFrameMaterial,
          ),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyHasInsulation].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasInsulation),
  );
  result[EDefaultVariableId.PropertyHasSolarPanels].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasSolarPanels),
  );
  result[EDefaultVariableId.PropertyIsLowEnergy].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isLowEnergy),
  );
  result[EDefaultVariableId.PropertyIsPassiveHouse].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isPassiveHouse),
  );
  result[EDefaultVariableId.PropertyCo2Emission].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.co2Emission),
  );
  result[EDefaultVariableId.PropertyELevel].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.eLevel),
  );
  result[EDefaultVariableId.PropertyKLevel].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.kLevel),
  );
  const propertyPetsAllowed = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.petsAllowed,
  );
  result[EDefaultVariableId.PropertyPetsAllowed].push(
    propertyPetsAllowed
      ? translateByTemplateLanguage(
          getOptionTranslationKey("yesNoUnknown", propertyPetsAllowed),
        )
      : undefined,
  );
  const propertyPetsAllowedDetail = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.petsAllowedDetail,
  );
  result[EDefaultVariableId.PropertyPetsAllowedDetail].push(
    propertyPetsAllowedDetail
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "petsAllowedDetail",
            propertyPetsAllowedDetail,
          ),
        )
      : undefined,
  );
  const propertySmokingAllowed = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.smokingAllowed,
  );
  result[EDefaultVariableId.PropertySmokingAllowed].push(
    propertySmokingAllowed
      ? translateByTemplateLanguage(
          getOptionTranslationKey("yesNoUnknown", propertySmokingAllowed),
        )
      : undefined,
  );
  const propertyCondition = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.condition,
  );
  result[EDefaultVariableId.PropertyCondition].push(
    propertyCondition
      ? translateByTemplateLanguage(
          getOptionTranslationKey("condition", propertyCondition),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyRenovationYear].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.renovationYear),
  );
  const propertyEnvironmentType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.environmentType,
  );
  result[EDefaultVariableId.PropertyEnvironmentType].push(
    propertyEnvironmentType
      ? translateByTemplateLanguage(
          getOptionTranslationKey("environmentType", propertyEnvironmentType),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyFrontWidth].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.frontWidth),
  );
  result[EDefaultVariableId.PropertyVolume].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.volume),
  );
  const propertyRoofType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.roofType,
  );
  result[EDefaultVariableId.PropertyRoofType].push(
    propertyRoofType
      ? translateByTemplateLanguage(
          getOptionTranslationKey("roofType", propertyRoofType),
        )
      : undefined,
  );
  const propertyRoofMaterial = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.roofMaterial,
  );
  result[EDefaultVariableId.PropertyRoofMaterial].push(
    propertyRoofMaterial
      ? translateByTemplateLanguage(
          getOptionTranslationKey("roofMaterial", propertyRoofMaterial),
        )
      : undefined,
  );
  const propertyFloodZoneGScore = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.floodZoneGScore,
  );
  result[EDefaultVariableId.PropertyFloodZoneGScore].push(
    propertyFloodZoneGScore
      ? translateByTemplateLanguage(
          getOptionTranslationKey("floodScore", propertyFloodZoneGScore),
        )
      : undefined,
  );
  const propertyFloodZonePScore = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.floodZonePScore,
  );
  result[EDefaultVariableId.PropertyFloodZonePScore].push(
    propertyFloodZonePScore
      ? translateByTemplateLanguage(
          getOptionTranslationKey("floodScore", propertyFloodZonePScore),
        )
      : undefined,
  );
  const propertyConformityCertificate = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.conformityCertificate,
  );
  result[EDefaultVariableId.PropertyConformityCertificate].push(
    propertyConformityCertificate
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "yesNoUnknown",
            propertyConformityCertificate,
          ),
        )
      : undefined,
  );
  const propertyElectricityCertificate = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.electricityCertificate,
  );
  result[EDefaultVariableId.PropertyElectricityCertificate].push(
    propertyElectricityCertificate
      ? translateByTemplateLanguage(
          getOptionTranslationKey(
            "electricityCertificate",
            propertyElectricityCertificate,
          ),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyAsbestosCertificate].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.asbestosCertificate),
  );
  result[EDefaultVariableId.PropertyAsbestosCertificateDate].push(
    getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.asbestosCertificateDate,
    ),
  );
  result[EDefaultVariableId.PropertyVirtualTourUrl].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.virtualTourUrl),
  );
  result[EDefaultVariableId.PropertyVideoUrl].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.videoUrl),
  );
  result[EDefaultVariableId.PropertyModel3dUrl].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.model3dUrl),
  );
  result[EDefaultVariableId.PropertyFloorPlanUrl].push(
    getAmenityStringValue(propertyAmenities, AMENITY_NAMES.floorPlanUrl),
  );

  if (contract) {
    result[EDefaultVariableId.RentDepositPrice].push(
      contract?.rentDepositAmount,
    );
    const rentDepositMonths =
      (contract.rentDepositAmount || 0) /
      getAmountWithVat(
        contract.currentPrice || 0,
        (contract.priceVat || 0) / 100,
      );
    if (round(rentDepositMonths) === rentDepositMonths) {
      result[EDefaultVariableId.RentDepositMonths].push(rentDepositMonths);
    }

    result[EDefaultVariableId.ContractStartDate].push(contract.startDate);
    result[EDefaultVariableId.ContractStopDate].push(contract.stopDate);
    result[EDefaultVariableId.ContractPaymentRepetition].push(
      translateByTemplateLanguage(
        `contract.payment_repetition.option.${contract.repetitionType}`.toLowerCase(),
      ).toLowerCase(),
    );

    result[EDefaultVariableId.ContractPriceVat].push(contract.priceVat);
    result[EDefaultVariableId.ContractStartPrice].push(contract.startPrice);
    result[EDefaultVariableId.ContractStartPriceIncludingVat].push(
      getAmountWithVat(contract.startPrice, (contract.priceVat || 0) / 100),
    );

    result[EDefaultVariableId.ContractCostsCommonVat].push(
      contract.costsCommonVat,
    );
    result[EDefaultVariableId.ContractCostsNonCommonVat].push(
      contract.costsNonCommonVat,
    );

    result[EDefaultVariableId.ContractStartCostsCommonFixed].push(
      getAmountWithVat(
        contract.startCostsCommon,
        (contract.costsCommonVat || 0) / 100,
      ),
    );
    result[EDefaultVariableId.ContractStartCostsCommonFixedIncludingVat].push(
      contract.startCostsCommon,
    );
    result[EDefaultVariableId.ContractStartCostsCommonProvision].push(
      contract.startCostsCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsCommonProvisionIncludingVat
    ].push(
      getAmountWithVat(
        contract.startCostsCommon,
        (contract.costsCommonVat || 0) / 100,
      ),
    );

    result[EDefaultVariableId.ContractStartCostsNonCommonFixed].push(
      contract.startCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsNonCommonFixedIncludingVat
    ].push(
      round(
        contract.startCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractStartCostsNonCommonProvision].push(
      contract.startCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsNonCommonProvisionIncludingVat
    ].push(
      round(
        contract.startCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );

    result[EDefaultVariableId.ContractCurrentPrice].push(contract.currentPrice);
    result[EDefaultVariableId.ContractCurrentPriceIncludingVat].push(
      getAmountWithVat(contract.currentPrice, (contract.priceVat || 0) / 100),
    );

    result[EDefaultVariableId.ContractCurrentCostsCommonFixed].push(
      contract.currentCostsCommon,
    );
    result[EDefaultVariableId.ContractCurrentCostsCommonFixedIncludingVat].push(
      round(
        contract.currentCostsCommon *
          (1 + (contract.costsCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractCurrentCostsCommonProvision].push(
      contract.currentCostsCommon,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsCommonProvisionIncludingVat
    ].push(
      round(
        contract.currentCostsCommon *
          (1 + (contract.costsCommonVat || 0) / 100),
        2,
      ),
    );

    result[EDefaultVariableId.ContractCurrentCostsNonCommonFixed].push(
      contract.currentCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsNonCommonFixedIncludingVat
    ].push(
      round(
        contract.currentCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractCurrentCostsNonCommonProvision].push(
      contract.costsNonCommonVat,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsNonCommonProvisionIncludingVat
    ].push(
      round(
        contract.currentCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );

    const {
      priceTypeCommonCosts = EContractCostsPriceType.Fixed,
      priceTypeNonCommonCosts = EContractCostsPriceType.Provision,
    } = contract;

    const priceTypeCostsCommon =
      contract.currentCostsCommon > 0
        ? translateByTemplateLanguage(
            `contract_info.costs.price_type.value.${priceTypeCommonCosts.toLowerCase()}`,
          )
        : translateByTemplateLanguage(
            "contract_info.costs.price_type.value.none",
          );
    result[EDefaultVariableId.ContractPriceTypeCostsCommon].push(
      priceTypeCostsCommon,
    );
    const priceTypeCostsNonCommon =
      contract.currentCostsNonCommon > 0
        ? translateByTemplateLanguage(
            `contract_info.costs.price_type.value.${priceTypeNonCommonCosts.toLowerCase()}`,
          )
        : translateByTemplateLanguage(
            "contract_info.costs.price_type.value.none",
          );

    result[EDefaultVariableId.ContractPriceTypeCostsNonCommon].push(
      priceTypeCostsNonCommon,
    );
    result[EDefaultVariableId.ContractStartIndexDate].push(
      new Date(
        contract.startIndexYear,
        contract.startIndexMonth - 1, // Zero-based
        1,
      ),
    );
    // Get start index
    result[EDefaultVariableId.ContractStartIndexNumber].push(
      undefined,
      // contract.startIndexId,
    );
    result[EDefaultVariableId.ContractDurationYears].push(
      contract.stopDate
        ? floor(
            differenceInDays(
              addDays(contract.stopDate, 1),
              contract.startDate,
            ) / 365,
          )
        : undefined,
    );

    if (indexInfoAvailable && indexInfoSimulation && indexInfoRequest) {
      const epcLabel = property.epcLabel || EPropertyEPCLabel.Unknown;

      // New price as filled in by the user
      const newPriceWithVat = getAmountWithVat(
        indexInfoRequest.newPrice,
        (contract.priceVat || 0) / 100,
      );

      const increaseRatio = indexInfoRequest.newPrice / contract.currentPrice;
      const newCommonCosts = contract.commonCostsAreIndexable
        ? contract.currentCostsCommon * increaseRatio
        : contract.currentCostsCommon;
      const newCommonCostsWithVat = getAmountWithVat(
        newCommonCosts,
        (contract.priceVat || 0) / 100,
      );
      const newNonCommonCosts = contract.nonCommonCostsAreIndexable
        ? contract.currentCostsNonCommon * increaseRatio
        : contract.currentCostsNonCommon;
      const newNonCommonCostsWithVat = getAmountWithVat(
        newNonCommonCosts,
        (contract.priceVat || 0) / 100,
      );

      // New price as proposed by Rentio (max. allowed increase)
      let proposedNewPrice = indexInfoAvailable.newPrice;
      let proposedCorrectionFactor = 1;
      if (
        indexInfoAvailable.epcBasedIndexationType ===
          EEpcBasedIndexationType.Corrected &&
        !indexInfoRequest.forceFullIndexation
      ) {
        if (epcLabel === EPropertyEPCLabel.D) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcD;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcD;
        } else if (epcLabel === EPropertyEPCLabel.E) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcE;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcE;
        } else if (epcLabel === EPropertyEPCLabel.F) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcF;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcF;
        } else if (epcLabel === EPropertyEPCLabel.G) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcG;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcG;
        } else if (
          epcLabel === EPropertyEPCLabel.NoEPCAvailable ||
          epcLabel === EPropertyEPCLabel.Unknown ||
          checkIfEpcExpired(contract.epcExpirationDate)
        ) {
          proposedNewPrice = indexInfoAvailable.newPriceNoEpc;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorNoEpc;
        }
      }

      const proposedNewPriceWithVat = getAmountWithVat(
        proposedNewPrice,
        (contract.priceVat || 0) / 100,
      );

      const proposedIncreaseRatio = proposedNewPrice / contract.currentPrice;
      const proposedNewCommonCosts = contract.commonCostsAreIndexable
        ? contract.currentCostsCommon * proposedIncreaseRatio
        : contract.currentCostsCommon;
      const proposedNewCommonCostsWithVat = getAmountWithVat(
        proposedNewCommonCosts,
        (contract.priceVat || 0) / 100,
      );
      const proposedNewNonCommonCosts = contract.nonCommonCostsAreIndexable
        ? contract.currentCostsNonCommon * proposedIncreaseRatio
        : contract.currentCostsNonCommon;
      const proposedNewNonCommonCostsWithVat = getAmountWithVat(
        proposedNewNonCommonCosts,
        (contract.priceVat || 0) / 100,
      );

      result[EDefaultVariableId.ContractIndexStartIndex].push(
        indexInfoAvailable.startIndex,
      );
      result[EDefaultVariableId.ContractIndexNewIndex].push(
        indexInfoAvailable.newIndex,
      );
      result[EDefaultVariableId.ContractIndexCorrectionFactor].push(
        proposedCorrectionFactor !== 1 ? proposedCorrectionFactor : undefined,
      );
      result[EDefaultVariableId.ContractIndexEpcBasedIndexType].push(
        indexInfoAvailable.epcBasedIndexationType,
      );

      result[EDefaultVariableId.ContractIndexNewPriceWasAdjusted].push(
        proposedNewPrice !== indexInfoRequest.newPrice,
      );

      result[EDefaultVariableId.ContractIndexNewPriceOriginal].push(
        round(proposedNewPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewCommonCostsOriginal].push(
        round(proposedNewCommonCostsWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal].push(
        round(proposedNewNonCommonCostsWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexNewPriceAdjusted].push(
        round(newPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewCommonCostsAdjusted].push(
        round(newCommonCostsWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted].push(
        round(newNonCommonCostsWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexTotalPriceOriginal].push(
        proposedNewPriceWithVat +
          getAmountWithVat(
            contract.currentCostsCommon,
            (contract.costsCommonVat || 0) / 100,
          ) +
          getAmountWithVat(
            contract.currentCostsNonCommon,
            (contract.costsNonCommonVat || 0) / 100,
          ),
      );
      result[EDefaultVariableId.ContractIndexTotalPriceAdjusted].push(
        newPriceWithVat +
          contract.currentCostsNonCommon +
          contract.currentCostsCommon,
      );

      result[EDefaultVariableId.ContractIndexForceFullIndexation].push(
        indexInfoRequest.forceFullIndexation,
      );

      const startNextPaymentPeriod =
        indexInfoAvailable.retroactivityPeriodStart || new Date();
      result[EDefaultVariableId.ContractIndexStartNextPaymentPeriod].push(
        startNextPaymentPeriod,
      );

      const now = new Date();

      result[EDefaultVariableId.ContractIndexRetroactivePayments].push(
        indexInfoRequest.isRetroactive,
      );

      // Only announce a correction period when a correction is actually owed; with
      // 0 months the period would print as an end date before its start (SUP-4320)
      const hasCorrection = indexInfoSimulation.correctionPrice > 0;

      result[EDefaultVariableId.ContractIndexCorrectionPeriodStart].push(
        hasCorrection ? indexInfoSimulation.correctionPeriodStart : undefined,
      );

      result[EDefaultVariableId.ContractIndexCorrectionPeriodEnd].push(
        hasCorrection ? indexInfoSimulation.correctionPeriodEnd : undefined,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodStart].push(
        indexInfoSimulation.retroactivityPeriodStart,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodEnd].push(
        indexInfoSimulation.retroactivityPeriodEnd,
      );

      const retroactivityPriceWithVat = getAmountWithVat(
        indexInfoSimulation.retroactivityPrice,
        (contract.priceVat || 0) / 100,
      );
      const correctionPriceWithVat = getAmountWithVat(
        indexInfoSimulation.correctionPrice,
        (contract.priceVat || 0) / 100,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodPrice].push(
        round(retroactivityPriceWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexCorrectionPeriodPrice].push(
        round(correctionPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexCorrectionAndRetroSum].push(
        round(retroactivityPriceWithVat + correctionPriceWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexPaymentsWithRentio].push(
        !!rentPaymentOrder,
      );

      const day = new Date(
        indexInfoSimulation.retroactivityPeriodStart ||
          indexInfoSimulation.correctionPeriodStart,
      ).getDate();

      const indexationStartAt = new Date(
        now.getFullYear(),
        now.getMonth(),
        day,
      );

      result[EDefaultVariableId.ContractIndexPaymentOrderNewDueDate].push(
        indexationStartAt,
      );
    } else {
      result[EDefaultVariableId.ContractIndexStartIndex].push(undefined);
      result[EDefaultVariableId.ContractIndexNewIndex].push(undefined);
      result[EDefaultVariableId.ContractIndexCorrectionFactor].push(undefined);
      result[EDefaultVariableId.ContractIndexEpcBasedIndexType].push(undefined);
      result[EDefaultVariableId.ContractIndexNewPriceWasAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewPriceOriginal].push(undefined);
      result[EDefaultVariableId.ContractIndexNewCommonCostsOriginal].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewPriceAdjusted].push(undefined);
      result[EDefaultVariableId.ContractIndexNewCommonCostsAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexForceFullIndexation].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexStartNextPaymentPeriod].push(
        undefined,
      );
      result[EDefaultVariableId.ContractRetroactivityPeriodStart].push(
        undefined,
      );
      result[EDefaultVariableId.ContractRetroactivityPeriodEnd].push(undefined);
      result[EDefaultVariableId.ContractRetroactivityPeriodPrice].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexPaymentsWithRentio].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexPaymentOrderNewDueDate].push(
        undefined,
      );
    }

    if (
      contract.ibanisationStatus === EContractIbanisationStatus.Enabled &&
      ibanisationBankAccount
    ) {
      result[EDefaultVariableId.ContractIbanisationBankAccountIban].push(
        formatIban(ibanisationBankAccount.iban),
      );
      result[EDefaultVariableId.ContractIbanisationBankAccountBic].push(
        ibanisationBankAccount.mangoWalletIbanBic,
      );
      result[EDefaultVariableId.ContractIbanisationBankAccountReference].push(
        contract.ibanisationReference,
      );
    }
  }

  // Additional costs
  const indexableCosts =
    contract?.additionalCosts?.filter(cost => cost?.isIndexable) || [];
  if (contract && indexableCosts?.length) {
    const increaseRatio = (
      contract?.currentPrice / contract?.startPrice
    ).toFixed(2);

    indexableCosts.forEach(cost => {
      const { currentPrice = 0, startPrice = 0, description } = cost;
      const appliedIncrease = round(currentPrice - startPrice, 2);

      result[EDefaultVariableId.AdditionalCostDescription].push(description);
      result[EDefaultVariableId.AdditionalCostStartPrice].push(startPrice);
      result[EDefaultVariableId.AdditionalCostCorrectionFactor].push(
        increaseRatio,
      );
      result[EDefaultVariableId.AdditionalCostProposedIncrease].push(
        appliedIncrease,
      );
      result[EDefaultVariableId.AdditionalCostNewPrice].push(currentPrice);
    });
  }

  Object.keys(preferences).forEach(preferenceKey => {
    if (!preferenceKey.startsWith("template_document:field:")) {
      return;
    }

    const variableId = preferenceKey.replace("template_document:field:", "");
    const variable = fields.variables.find(x => x.id === variableId);
    if (!variable?.persisted) {
      return;
    }

    const data = preferences[preferenceKey];
    if (!data || !Array.isArray(data)) {
      return;
    }

    // @ts-ignore
    result[variableId] = data;
  });

  return JSON.stringify({
    ...result,
    ...variablesDataToOverride,
  });
};

export const contactIsValidForTemplate = (contact: IContact) => {
  if (!contact.placeOfBirth) {
    return false;
  }

  if (!contact.dateOfBirth) {
    return false;
  }

  if (!contact.nationality) {
    return false;
  }

  if (contact.nationality !== "BE") {
    if (!contact.nationalRegisterNumber && !contact.foreignIdentityNumber) {
      return false;
    }
  }

  if (contact.nationality === "BE" && !contact.nationalRegisterNumber) {
    return false;
  }

  return true;
};

/**
 * The default variables a duplicate carries over from the template document it
 * was duplicated from: the broker's own details, plus who draws up the location
 * description. Any default variable not listed here is left to
 * `getDefaultVariablesData`, which fills it in for the new document.
 */
const CARRIED_OVER_DEFAULT_VARIABLE_IDS = [
  EDefaultVariableId.LocationDescriptionPerson,

  EDefaultVariableId.BrokerEnabled,
  EDefaultVariableId.BrokerBiv,
  EDefaultVariableId.BrokerOfficeName,
  EDefaultVariableId.BrokerFullName,
  EDefaultVariableId.BrokerFirstName,
  EDefaultVariableId.BrokerLastName,
  EDefaultVariableId.BrokerBirthPlace,
  EDefaultVariableId.BrokerBirthDate,
  EDefaultVariableId.BrokerAddressFull,
  EDefaultVariableId.BrokerAddressStreet,
  EDefaultVariableId.BrokerAddressNumber,
  EDefaultVariableId.BrokerAddressBox,
  EDefaultVariableId.BrokerAddressZip,
  EDefaultVariableId.BrokerAddressCity,
  EDefaultVariableId.BrokerAddressCountry,
  EDefaultVariableId.BrokerLogo,
  EDefaultVariableId.BrokerIsSignerOnBehalfOfOwners,
  EDefaultVariableId.BrokerSignature,
  EDefaultVariableId.BrokerCivilLiabilityAtCompany,
  EDefaultVariableId.BrokerCivilLiabilityPolicyNumber,
];

/**
 * The values a duplicate carries over from the template document it was
 * duplicated from: everything the broker authored, and nothing that belongs to
 * the source document's tenant round. A tenant-editable variable of the source
 * document is dropped, because that is where the tenant who filled that
 * document in put their answers, and an incoming property inspection report
 * used to arrive carrying the departing tenant's remarks (SUP-4329).
 *
 * `editableByTenant` means "the tenant may edit this", not "the tenant wrote
 * this", and there is no per-value provenance to tell the two apart. So a
 * value the broker pre-filled into a tenant-editable variable is dropped as
 * well, and the duplicate starts blank there — for every template type the
 * duplicate flow offers, not only a property inspection report. That is the
 * trade the ticket asks for ("remark variables blank"); the alternative
 * carries the previous tenant's answers, which is the defect.
 */
export const getVariablesDataToCarryOver = (params: {
  fields: IFields;
  variablesData: IVariablesData;
}) => {
  const { fields, variablesData } = params;

  const tenantEditableVariableIds = getTenantEditableVariableIds(fields);
  const definedVariableIds = fields.variables.map(x => x.id);

  const result: IVariablesData = {};

  Object.keys(variablesData).forEach(key => {
    // Answered by the tenant of the source document
    if (tenantEditableVariableIds.includes(key)) {
      return;
    }

    // A custom variable the source document no longer defines. Removing a
    // variable in the editor drops it from `fields` and leaves its value behind
    // (`Fields.context.tsx`, `removeVariable`), so a value orphaned this way may
    // be an answer the departing tenant gave while the variable was still
    // tenant-editable, and nothing left on the record says which it was.
    //
    // The trade: a value the broker authored and then orphaned the same way is
    // dropped too. That one is recoverable - the source document does not render
    // it either, so the broker is not looking at it, and they can retype it -
    // while carrying a tenant's remark into the next tenant's document is the
    // defect this ticket is about. Default variable ids are excluded because
    // they are never required to appear in `fields.variables`;
    // `getDefaultVariablesData` seeds all of them, and the rule below decides
    // which of those carry over.
    if (
      !definedVariableIds.includes(key) &&
      !DEFAULT_VARIABLE_IDS.includes(key)
    ) {
      return;
    }

    // A default variable that is not carried over
    const isDefaultVariable = DEFAULT_VARIABLE_IDS.includes(key);
    if (
      isDefaultVariable &&
      !CARRIED_OVER_DEFAULT_VARIABLE_IDS.includes(key as EDefaultVariableId)
    ) {
      return;
    }

    result[key] = variablesData[key];
  });

  return result;
};

/**
 * The field definitions of the template document a duplicate is being made
 * from, or `undefined` when the stored `fields` column cannot be read as field
 * definitions at all.
 *
 * The duplicate has to treat that `undefined` as a stop. `parseFields` answers
 * anything it cannot read by logging and returning an empty field list, and an
 * empty list makes every stored value look broker-authored to
 * `getVariablesDataToCarryOver`, which is how the previous tenant's answers
 * would be carried over again (SUP-4329). A template with genuinely no
 * variables is a different thing and parses fine, so it still duplicates.
 */
export const getFieldsForDuplicate = (params: {
  fields: ITemplateDocumentDetail["fields"];
}) => {
  const { fields } = params;

  // A column that was never stored is as unreadable as one that does not
  // parse. `parseFields(undefined)` answers with an empty field list, which is
  // truthy, so without this the caller's stop never fires and every stored
  // value is carried over as broker-authored.
  if (!fields) {
    return undefined;
  }

  if (typeof fields !== "string") {
    return parseFields(fields);
  }

  try {
    const parsed = JSON.parse(fields);

    // Valid JSON that is not an object carries no definitions either, and
    // `parseFields` would spread it into an empty field list.
    if (typeof parsed !== "object" || parsed === null) {
      logError({
        error: new Error(
          "Template document fields parsed to a value that is not an object, so it carries no field definitions",
        ),
        // `typeof null` is "object", and null reaches this branch too.
        parameters: { parsedType: parsed === null ? "null" : typeof parsed },
      });

      return undefined;
    }

    return parseFields(parsed);
  } catch (unknownError) {
    // `parseFields` logs its own parse failures, and this helper took that
    // parse over, so it has to keep the Sentry event with it: the alert the
    // caller shows the user is generic, and this is the only trace of what the
    // stored column actually contained.
    const error = unknownError as Error;
    logError({ error });

    return undefined;
  }
};
